import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, Play, ShieldCheck } from "lucide-react";
import { auth, loginWithGoogle } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function errText(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function Login() {
  const { setUser } = useAuth() || {};
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [reviewerBusy, setReviewerBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await auth.login(email.trim(), password);
      setUser?.(u);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const reviewerLogin = async () => {
    setError("");
    setReviewerBusy(true);
    try {
      const u = await auth.reviewerLogin();
      setUser?.(u);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errText(err?.response?.data?.detail) || err.message);
    }
    setReviewerBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-5 text-white" data-testid="login-page">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#00F0FF]">
            <Play size={17} className="fill-black text-black" />
          </div>
          <span className="font-display text-xl font-black tracking-tight">Caption Motion</span>
        </Link>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7">
          <h1 className="font-display text-2xl font-black tracking-tight">Sign in</h1>
          <p className="mt-1 text-sm text-[#A1A1AA]">Access your Caption Motion workspace.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Email</label>
              <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@example.com"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#00F0FF]/60" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#A1A1AA]">Password</label>
              <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="••••••••"
                className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#00F0FF]/60" />
            </div>

            {error && <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}

            <button type="submit" disabled={busy} data-testid="login-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#00F0FF] py-3 text-sm font-black text-black transition-transform hover:scale-[1.02] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Sign in
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-[#666]">
            <div className="h-px flex-1 bg-white/10" /> or <div className="h-px flex-1 bg-white/10" />
          </div>

          <button onClick={loginWithGoogle} data-testid="login-google"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] py-3 text-sm font-bold text-white transition-colors hover:border-white/30">
            Continue with Google
          </button>

          <button onClick={reviewerLogin} disabled={reviewerBusy} data-testid="reviewer-login-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 py-3 text-sm font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/20 disabled:opacity-60">
            {reviewerBusy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Sign in as reviewer
          </button>
          <p className="mt-2 text-center text-[11px] text-[#666]">One-tap demo access for payment verification — no credentials needed.</p>
        </div>
      </div>
    </div>
  );
}
